import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { sanitizeAssetUrl, sanitizeHtmlContent, sanitizeNavigationTarget } from '../../utils/contentSecurity';
import { getGradientStyle } from '../../utils/gradientStyles';

export const DEFAULT_BANNER_LIGHT_GRADIENT = 'bg-gradient-to-r rtl:bg-gradient-to-l from-sky-50 via-white to-amber-50';
export const DEFAULT_BANNER_DARK_GRADIENT = 'bg-gradient-to-r rtl:bg-gradient-to-l from-gray-950 via-slate-900 to-blue-950';

const BannerCarousel = ({ slides }) => {
    const [[page, direction], setPage] = useState([0, 0]);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const navigate = useNavigate();
    const autoPlayTimerRef = useRef(null);

    const currentIndex = slides?.length ? ((page % slides.length) + slides.length) % slides.length : 0;

    // Swipe Sensitivity Thresholds
    const swipeConfidenceThreshold = 10000;
    const swipePower = (offset, velocity) => Math.abs(offset) * velocity;

    // Define paginate with useCallback so it's stable across renders
    const paginate = useCallback((newDirection) => {
        setPage(([currPage]) => [currPage + newDirection, newDirection]);
    }, []);

    // Reset auto-play: cancel any pending timer, pause, then resume after 5s
    const resetAutoPlay = useCallback(() => {
        if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current);
        setIsAutoPlaying(false);
        autoPlayTimerRef.current = setTimeout(() => setIsAutoPlaying(true), 5000);
    }, []);

    // Auto-play logic — safe with cleanup
    useEffect(() => {
        if (!slides || slides.length <= 1 || !isAutoPlaying) return;
        const interval = setInterval(() => paginate(1), 5000);
        return () => clearInterval(interval);
    }, [slides, isAutoPlaying, paginate]);

    // Cleanup the resetAutoPlay timeout on unmount
    useEffect(() => {
        return () => {
            if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current);
        };
    }, []);

    if (!slides || slides.length === 0) return null;

    const currentSlide = slides[currentIndex];
    const safeAction = sanitizeNavigationTarget(currentSlide?.action);
    const safeImage = sanitizeAssetUrl(currentSlide?.image);
    const safeTitle = currentSlide?.title ? sanitizeHtmlContent(currentSlide.title) : '';
    const usesGradientBackground = !safeImage;
    const rawGradient = String(currentSlide?.gradient || currentSlide?.lightGradient || currentSlide?.backgroundGradient || currentSlide?.lightBackground || '').trim();
    const rawDarkGradient = String(currentSlide?.darkGradient || currentSlide?.gradientDark || '').trim();
    const likelyDarkGradient = /\b(?:from|via|to)-(?:black|gray-(?:7|8|9)\d{2}|gray-950|slate-(?:7|8|9)\d{2}|slate-950|zinc-(?:7|8|9)\d{2}|zinc-950|neutral-(?:7|8|9)\d{2}|neutral-950|stone-(?:7|8|9)\d{2}|stone-950)\b/.test(rawGradient);
    const lightGradientClass = rawGradient && !likelyDarkGradient ? rawGradient : DEFAULT_BANNER_LIGHT_GRADIENT;
    const darkGradientClass = rawDarkGradient || (rawGradient && likelyDarkGradient ? rawGradient : DEFAULT_BANNER_DARK_GRADIENT);
    const lightGradientStyle = getGradientStyle(lightGradientClass);
    const darkGradientStyle = getGradientStyle(darkGradientClass);

    const overlayClass = usesGradientBackground
        ? 'absolute inset-0 bg-gradient-to-r rtl:bg-gradient-to-l from-white/90 via-white/50 to-transparent pointer-events-none dark:from-black/75 dark:via-black/35'
        : 'absolute inset-0 bg-gradient-to-r rtl:bg-gradient-to-l from-black/80 via-black/40 to-transparent pointer-events-none';
    const badgeClass = usesGradientBackground
        ? 'inline-block px-2 py-0.5 md:px-3 md:py-1 rounded-full bg-white/80 text-primary-700 text-[9px] md:text-xs font-bold uppercase tracking-wider w-fit mb-2 md:mb-3 border border-primary-200 shadow-sm dark:bg-white/20 dark:text-white dark:border-white/10'
        : 'inline-block px-2 py-0.5 md:px-3 md:py-1 rounded-full bg-white/20 backdrop-blur-md text-white text-[9px] md:text-xs font-bold uppercase tracking-wider w-fit mb-2 md:mb-3 border border-white/10';
    const titleClass = usesGradientBackground
        ? 'text-xl md:text-5xl font-black text-gray-950 dark:text-white mb-1 md:mb-3 leading-tight'
        : 'text-xl md:text-5xl font-black text-white mb-1 md:mb-3 leading-tight';
    const subtitleClass = usesGradientBackground
        ? 'text-xs md:text-lg text-gray-700 dark:text-gray-200 mb-3 md:mb-6 font-medium max-w-md leading-relaxed'
        : 'text-xs md:text-lg text-gray-200 mb-3 md:mb-6 font-medium max-w-md leading-relaxed';
    const actionButtonClass = usesGradientBackground
        ? 'px-4 py-2 md:px-6 md:py-3 bg-primary-600 text-white rounded-lg md:rounded-xl text-xs md:text-base font-bold w-fit hover:bg-primary-700 hover:scale-105 active:scale-95 transition-all shadow-xl hover:shadow-2xl flex items-center gap-2 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100'
        : 'px-4 py-2 md:px-6 md:py-3 bg-white text-gray-900 rounded-lg md:rounded-xl text-xs md:text-base font-bold w-fit hover:bg-gray-100 hover:scale-105 active:scale-95 transition-all shadow-xl hover:shadow-2xl flex items-center gap-2';

    const handleAction = (slide) => {
        const target = sanitizeNavigationTarget(slide?.action);
        if (!target) return;

        if (/^https?:\/\//i.test(target)) {
            window.open(target, '_blank', 'noopener,noreferrer');
        } else if (/^(mailto:|tel:)/i.test(target)) {
            window.location.assign(target);
        } else {
            navigate(target);
        }
    };

    const onDragEnd = (e, { offset, velocity }) => {
        const swipe = swipePower(offset.x, velocity.x);
        if (swipe < -swipeConfidenceThreshold) {
            paginate(1);
            resetAutoPlay();
        } else if (swipe > swipeConfidenceThreshold) {
            paginate(-1);
            resetAutoPlay();
        }
    };

    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? '100%' : '-100%',
            opacity: 0,
            scale: 0.95
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1,
            scale: 1
        },
        exit: (direction) => ({
            zIndex: 0,
            x: direction < 0 ? '100%' : '-100%',
            opacity: 0,
            scale: 0.95
        })
    };

    return (
        <div className="relative w-full overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100 aspect-[18/9] dark:bg-gray-950 dark:ring-gray-800 group">
            <AnimatePresence initial={false} custom={direction}>
                <motion.div
                    key={page}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                        x: { type: "spring", stiffness: 300, damping: 30 },
                        opacity: { duration: 0.2 }
                    }}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={1}
                    onDragStart={() => {
                        setIsAutoPlaying(false);
                        if (autoPlayTimerRef.current) clearTimeout(autoPlayTimerRef.current);
                    }}
                    onDragEnd={onDragEnd}
                    className="absolute inset-0 w-full h-full cursor-grab active:cursor-grabbing touch-pan-y"
                >
                    {/* Background Image */}
                    {safeImage ? (
                        <img
                            src={safeImage}
                            alt={currentSlide.title || 'Banner'}
                            className="w-full h-full object-cover pointer-events-none"
                        />
                    ) : (
                        <>
                            <div className={`w-full h-full pointer-events-none dark:hidden ${lightGradientClass}`} style={lightGradientStyle} />
                            <div className={`hidden w-full h-full pointer-events-none dark:block ${darkGradientClass}`} style={darkGradientStyle} />
                        </>
                    )}

                    {/* Overlay */}
                    <div className={overlayClass} />

                    {/* Content */}
                    <div className="absolute inset-0 flex flex-col justify-center ps-6 md:ps-16 max-w-2xl">
                        {currentSlide.badge && (
                            <span className={badgeClass}>
                                {currentSlide.badge}
                            </span>
                        )}

                        {currentSlide.title && (
                            <h2
                                className={titleClass}
                                dangerouslySetInnerHTML={{ __html: safeTitle }}
                            />
                        )}

                        {currentSlide.subtitle && (
                            <p className={subtitleClass}>
                                {currentSlide.subtitle}
                            </p>
                        )}

                        {currentSlide.buttonText && safeAction && (
                            <button
                                onClick={() => handleAction(currentSlide)}
                                className={actionButtonClass}
                            >
                                {currentSlide.buttonText}
                                {/^(https?:\/\/|mailto:|tel:)/i.test(safeAction) && <ExternalLink size={14} className="md:w-4 md:h-4 rtl:-scale-x-100" />}
                            </button>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons (Hidden on mobile, swipe/dots used) */}
            {slides.length > 1 && (
                <>
                    <button
                        onClick={(e) => { e.stopPropagation(); paginate(-1); resetAutoPlay(); }}
                        className="hidden md:flex absolute start-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/10 transition-all opacity-0 group-hover:opacity-100 z-20"
                    >
                        <ChevronLeft size={20} className="rtl:-scale-x-100" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); paginate(1); resetAutoPlay(); }}
                        className="hidden md:flex absolute end-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/10 transition-all opacity-0 group-hover:opacity-100 z-20"
                    >
                        <ChevronRight size={20} className="rtl:-scale-x-100" />
                    </button>

                    {/* Dots Indicators */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
                        {slides.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => {
                                    const dir = idx > currentIndex ? 1 : -1;
                                    setPage([idx, dir]);
                                    resetAutoPlay();
                                }}
                                className={`h-1.5 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-5' : 'bg-white/30 w-1.5 hover:bg-white/50'}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default BannerCarousel;
